import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { ScoreDisplay } from '../components/ScoreDisplay';
import { AlertBadge } from '../components/AlertBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Calendar, 
  Clock,
  ArrowRight,
  Loader2,
  FileText
} from 'lucide-react';
import { formatDate, formatCurrency } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [todayEntry, setTodayEntry] = useState(null);
  const [todayScorecard, setTodayScorecard] = useState(null);
  const [recentScorecards, setRecentScorecards] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch today's entry
      const entriesRes = await axios.get(`${API}/entries`, {
        params: { start_date: today, end_date: today }
      });
      
      if (entriesRes.data.length > 0) {
        setTodayEntry(entriesRes.data[0]);
        
        // Fetch scorecard for today
        const scorecardRes = await axios.get(`${API}/scorecards/${entriesRes.data[0].id}`);
        setTodayScorecard(scorecardRes.data);
      }
      
      // Fetch recent scorecards
      const scorecardsRes = await axios.get(`${API}/scorecards`);
      setRecentScorecards(scorecardsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="manager-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600 mt-1">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Quick Actions */}
        {!todayEntry && (
          <Card className="mb-8 border-violet-200 bg-violet-50/50">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-slate-900">No entry for today</h3>
                  <p className="text-sm text-slate-600">Submit your daily numbers to generate today's scorecard</p>
                </div>
                <Button asChild className="bg-violet-600 hover:bg-violet-700">
                  <Link to="/entry" data-testid="new-entry-cta">
                    <Plus className="w-4 h-4 mr-2" />
                    New Entry
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Scorecard */}
        {todayScorecard && (
          <Card className="mb-8 overflow-hidden" data-testid="today-scorecard">
            <CardHeader className="bg-slate-50 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Today's Scorecard</CardTitle>
                <Badge 
                  variant="outline"
                  className={todayScorecard.status === 'final' 
                    ? 'border-emerald-500 text-emerald-700 bg-emerald-50' 
                    : 'border-amber-500 text-amber-700 bg-amber-50'
                  }
                >
                  {todayScorecard.status === 'final' ? 'Final' : 'Preliminary – PAX Pending'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <ScoreDisplay score={todayScorecard.overall_score} size="large" />
                
                <div className="flex-1 space-y-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500">Budget</p>
                      <p className="font-mono font-semibold">{formatCurrency(todayScorecard.raw_data.daily_sales_budget)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500">Actual Sales</p>
                      <p className="font-mono font-semibold">{formatCurrency(todayScorecard.raw_data.actual_total_sales)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500">Transactions</p>
                      <p className="font-mono font-semibold">{todayScorecard.raw_data.transactions}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500">PAX</p>
                      <p className="font-mono font-semibold">{todayScorecard.raw_data.pax || '—'}</p>
                    </div>
                  </div>
                  
                  <AlertBadge alerts={todayScorecard.alerts} />
                  
                  <Button asChild variant="outline" className="w-full sm:w-auto">
                    <Link to={`/scorecards/${todayEntry?.id}`}>
                      View Full Scorecard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Scorecards */}
        <Card data-testid="recent-scorecards">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-400" />
              Recent Scorecards
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentScorecards.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No scorecards yet. Submit your first entry!</p>
            ) : (
              <div className="space-y-3">
                {recentScorecards.map((sc, index) => (
                  <Link 
                    key={sc.entry_id}
                    to={`/scorecards/${sc.entry_id}`}
                    className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-colors"
                    data-testid={`scorecard-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-900">{formatDate(sc.date)}</span>
                        <span className="text-xs text-slate-500">{sc.location_name}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Badge 
                        variant="outline"
                        className={sc.status === 'final' 
                          ? 'border-emerald-500 text-emerald-700' 
                          : 'border-amber-500 text-amber-700'
                        }
                      >
                        {sc.status}
                      </Badge>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-mono font-bold text-white ${
                        sc.overall_score >= 85 ? 'bg-emerald-500' :
                        sc.overall_score >= 70 ? 'bg-lime-500' :
                        sc.overall_score >= 55 ? 'bg-amber-500' : 'bg-red-500'
                      }`}>
                        {Math.round(sc.overall_score)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
